import { Link } from "react-router-dom";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ScrollReveal } from "../components/Reveal";

const faqs = [
  {
    q: "What services does The Visa Atelier provide?",
    a: "We provide professional administrative support for visa, passport and document applications, including UK visa applications, British passport applications and renewals, OCI applications, Indian passport services, business visa documentation and sponsor licence administration support.",
  },
  {
    q: "Do you provide immigration advice?",
    a: "The Visa Atelier provides administrative application support. We help organise documents, complete forms using information supplied by the client, review application packs and assist with appointments and uploads. Where regulated immigration advice or legal representation is required, clients should use an appropriately authorised immigration adviser or solicitor.",
  },
  {
    q: "How do I get started?",
    a: "Contact us by phone or WhatsApp on +44 7756 766759. We will discuss the service you need, explain the administrative process and provide a document checklist and quotation.",
  },
  {
    q: "Do you offer online appointments?",
    a: "Yes. Consultations can be provided remotely by telephone, WhatsApp or video call, subject to availability.",
  },
  {
    q: "What documents will I need?",
    a: "The required documents depend on the type of application. Once your service is confirmed, we will provide a personalised checklist based on the information you supply.",
  },
  {
    q: "Can you complete the application form for me?",
    a: "Yes. We can complete application forms using the information and documents you provide. You must check and approve all information before submission.",
  },
  {
    q: "Can you review an application I have already started?",
    a: "Yes. We can review the form and supporting documents for completeness, consistency and administrative accuracy.",
  },
  {
    q: "Can you book my biometric or passport appointment?",
    a: "Yes. We can assist with appointment booking, subject to availability on the relevant government or application-centre system.",
  },
  {
    q: "Do you help with British passport applications and renewals?",
    a: "Yes. We assist with first British passport applications, adult and child renewals, supporting-document preparation and appointment administration where applicable.",
  },
  {
    q: "Can you help with Fast Track passport renewal?",
    a: "We can assist with the administrative process and appointment search for available Fast Track services. Appointment availability and eligibility are controlled by HM Passport Office and cannot be guaranteed.",
  },
  {
    q: "Do you assist with UK Student Visa applications?",
    a: "Yes. We provide administrative support with application forms, document checklists, CAS-related document organisation, uploads and biometric appointment preparation.",
  },
  {
    q: "Do you support business and sponsor licence applications?",
    a: "Yes. We provide administrative support for business visa documentation, sponsor licence application preparation, document organisation and related corporate paperwork. Regulated immigration advice is not included unless provided separately by an authorised professional.",
  },
  {
    q: "Do you help with OCI and Indian passport applications?",
    a: "Yes. We assist with OCI applications, Indian passport renewals, surrender certificates, police clearance certificates and related administrative documentation.",
  },
  {
    q: "Do you support visas for countries outside the UK?",
    a: "Yes. We may provide administrative application support for selected international tourist, business, student and work visa applications. Availability depends on the destination and service required.",
  },
  {
    q: "How long does the process take?",
    a: "Our preparation time depends on the complexity of the application and how quickly you provide the required information. Government processing times are separate and are determined by the relevant authority.",
  },
  {
    q: "Can you guarantee my application will be approved?",
    a: "No. All decisions are made by the relevant government, embassy, passport office or immigration authority. Our service is for administrative preparation and support only.",
  },
  {
    q: "What are your fees?",
    a: "Fees vary depending on the application type, complexity and level of support required. You will receive a clear quotation before work begins. Government, embassy, appointment, courier and third-party charges are separate.",
  },
  {
    q: "When is payment required?",
    a: "Payment is normally required before work begins unless another arrangement has been agreed in writing.",
  },
  {
    q: "Are your service fees refundable?",
    a: "Service fees are generally non-refundable once work has started. Full details are provided in our Terms & Conditions and Cancellation and Refund Policy.",
  },
  {
    q: "Is my information confidential?",
    a: "Yes. We handle personal information and documents confidentially and process data in accordance with our Privacy Policy and applicable UK data-protection law.",
  },
  {
    q: "Can you submit the application on my behalf?",
    a: "Where technically permitted, we may provide submission support. However, you remain responsible for reviewing and approving all information before submission.",
  },
  {
    q: "Can you help if my application has already been refused?",
    a: "We can assist with basic administrative document organisation. Where advice on a refusal, appeal, review or legal strategy is required, you should contact an appropriately authorised immigration adviser or solicitor.",
  },
  {
    q: "Do you provide translation or certification services?",
    a: "We can coordinate translation, certification, courier and other third-party services where available. Any third-party fees are charged separately.",
  },
];

export default function FAQs() {
  return (
    <div className="relative bg-navy min-h-screen" data-testid="faqs-page">
      <div className="grain" />
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* Masthead */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
            <ScrollReveal className="col-span-12 md:col-span-8">
              <h1 className="font-display font-light text-ivory text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight">
                Frequently asked<br />
                <span className="italic text-gold">questions.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-4" delay={0.15}>
              <div className="font-mono-ui text-xs tracking-[0.3em] uppercase text-ivory/60 mb-3">
                {faqs.length} answers
              </div>
              <p className="text-ivory/70 text-base md:text-lg leading-relaxed">
                Everything you need to know about our administrative
                application support, fees, confidentiality and process.
                Can&rsquo;t find your question? <Link to="/#book" className="text-gold hover:underline">Ask us directly.</Link>
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Accordion */}
        <section className="bg-navy-800 border-y border-ivory/10 py-16 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <Accordion type="single" collapsible className="w-full" data-testid="faqs-accordion">
              {faqs.map((f, idx) => (
                <ScrollReveal key={f.q} delay={idx * 0.02} y={16}>
                  <AccordionItem
                    value={`item-${idx}`}
                    className="border-b border-ivory/10 last:border-0"
                    data-testid={`faq-item-${idx}`}
                  >
                    <AccordionTrigger className="py-6 md:py-8 text-left hover:no-underline [&[data-state=open]]:text-gold group">
                      <div className="flex items-start gap-6 md:gap-8 w-full pr-6">
                        <span className="font-mono-ui text-xs md:text-sm tracking-[0.2em] text-gold tabular-nums mt-1 md:mt-2 min-w-[38px]">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-xl md:text-2xl lg:text-3xl leading-tight text-ivory group-data-[state=open]:text-gold transition-colors">
                          {f.q}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 md:pb-10 pt-2 pl-[54px] md:pl-[62px] pr-6">
                      <p className="text-ivory/80 text-base md:text-lg leading-relaxed max-w-3xl">
                        {f.a}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </ScrollReveal>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="font-mono-ui text-xs tracking-[0.35em] text-gold uppercase mb-3">Still have questions?</div>
              <h2 className="font-display font-light text-ivory text-4xl md:text-6xl leading-[0.95] tracking-tight">
                We&rsquo;re happy to <span className="italic text-gold">help.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#book"
                className="inline-flex items-center gap-4 px-8 py-4 bg-gold text-navy font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-ivory transition-colors"
                data-testid="faqs-cta-enquire"
              >
                Send an Enquiry →
              </Link>
              <a
                href="tel:+447756766759"
                className="inline-flex items-center gap-4 px-8 py-4 border border-gold text-gold font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-gold hover:text-navy transition-colors"
                data-testid="faqs-cta-call"
              >
                Call +44 7756 766759
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
